import { Box } from '@mui/material';
import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import { Modality } from '~/domain/dicom/modality';
import { Orientations } from '~/engine/view/views/PlaneView';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import {
    blendImageState,
    primaryImageState,
} from '~/state/atoms/image/imageAtoms';
import {
    blendImageValueAtCursorState,
    coordinateDisplayExpandedState,
    currentImageSliceOrientationPositionsState,
    doseIntensityValueAtCursorState,
    imageValueAtCursorState,
    patientSpaceCoordsState,
} from '~/state/atoms/viewerAtoms';
import { OverlayLabel, OverlayRow } from './OverlayLabel';

interface CoordinateDisplayProps {
    viewportOrientation: Orientations;
    isActive: boolean;
}

const CoordinateDisplay = ({
    viewportOrientation,
    isActive,
}: CoordinateDisplayProps) => {
    const primaryImage = useRecoilValue(primaryImageState);
    const blendImage = useRecoilValue(blendImageState);
    const patientSpaceCoords = useRecoilValue(patientSpaceCoordsState);
    const currentImageSliceOrientationPositions =
        useRecoilValue<ImageSlicePositions>(
            currentImageSliceOrientationPositionsState
        );
    const imageValueAtCursor = useRecoilValue(imageValueAtCursorState);
    const blendImageValueAtCursor = useRecoilValue(
        blendImageValueAtCursorState
    );
    const doseIntensityValueAtCursor = useRecoilValue(
        doseIntensityValueAtCursorState
    );
    const doseVisibility = useRecoilValue(doseVisibilityState);
    const [coordinateDisplayExpanded, setCoordinateDisplayExpanded] =
        useRecoilState(coordinateDisplayExpandedState);
    const expanded = !!(
        coordinateDisplayExpanded?.includes(viewportOrientation) &&
        primaryImage?.firstSlice &&
        patientSpaceCoords?.coords
    );

    const handleExpand = () => {
        if (expanded) {
            const newExpanded = coordinateDisplayExpanded?.filter(
                (o) => o !== viewportOrientation
            );
            setCoordinateDisplayExpanded(
                newExpanded?.length ? newExpanded : null
            );
        } else {
            setCoordinateDisplayExpanded([
                ...(coordinateDisplayExpanded || []),
                viewportOrientation,
            ]);
        }
    };

    const axis =
        viewportOrientation === Orientations.SAGITTAL_ORIENTATION
            ? 'X'
            : viewportOrientation === Orientations.TRANSVERSAL_ORIENTATION
              ? 'Z'
              : 'Y';

    const primaryPosition =
        currentImageSliceOrientationPositions[
            viewportOrientation as keyof ImageSlicePositions
        ];

    const formattedImageValue =
        imageValueAtCursor !== null && primaryImage?.seriesMetadata.modality
            ? `${imageValueAtCursor}${
                  primaryImage?.seriesMetadata.modality === Modality.CT
                      ? ' HU'
                      : ''
              }`
            : null;

    const formattedBlendImageValue =
        blendImageValueAtCursor !== null && blendImage?.seriesMetadata.modality
            ? `${blendImageValueAtCursor}${
                  blendImage?.seriesMetadata.modality === Modality.CT
                      ? ' HU'
                      : ''
              }`
            : null;

    const formattedDoseValue =
        doseVisibility && doseIntensityValueAtCursor !== null
            ? `${doseIntensityValueAtCursor.toFixed(1)} Gy`
            : null;

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={0.25}
            onClick={handleExpand}
            sx={{
                cursor: 'pointer',
            }}
        >
            {formattedDoseValue && isActive && expanded && (
                <OverlayRow>
                    <Box
                        display="grid"
                        gridTemplateColumns={'auto 1fr'}
                        gap={0.5}
                    >
                        <OverlayLabel color="primary.light">D: </OverlayLabel>
                        <OverlayLabel
                            sx={{
                                width: 54,
                                justifyContent: 'flex-end',
                            }}
                        >
                            {formattedDoseValue}
                        </OverlayLabel>
                    </Box>
                </OverlayRow>
            )}

            {expanded &&
                isActive &&
                (formattedImageValue || formattedBlendImageValue) && (
                    <OverlayRow>
                        {formattedBlendImageValue ? (
                            <Box
                                display="grid"
                                gridTemplateColumns={'auto 1fr auto 1fr'}
                                gap={0.5}
                            >
                                <OverlayLabel color="primary.light">
                                    I (P / S) :{' '}
                                </OverlayLabel>
                                <OverlayLabel>
                                    {formattedImageValue
                                        ? ` ${formattedImageValue} `
                                        : ''}
                                </OverlayLabel>
                                <OverlayLabel sx={{ ml: 1, mr: 0 }}>
                                    /
                                </OverlayLabel>
                                <OverlayLabel>
                                    {formattedBlendImageValue}
                                </OverlayLabel>
                            </Box>
                        ) : (
                            <Box
                                display="grid"
                                gridTemplateColumns={'auto 1fr'}
                                gap={0.5}
                            >
                                <OverlayLabel color="primary.light">
                                    I:{' '}
                                </OverlayLabel>
                                <OverlayLabel
                                    sx={{
                                        width: 54,
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {formattedImageValue}
                                </OverlayLabel>
                            </Box>
                        )}
                    </OverlayRow>
                )}

            <OverlayRow>
                {expanded && isActive ? (
                    patientSpaceCoords?.coords && (
                        <React.Fragment>
                            {['X', 'Y', 'Z'].map((coord, index) => (
                                <Box
                                    key={coord}
                                    display="grid"
                                    gridTemplateColumns={'auto 1fr auto'}
                                    gap={0.5}
                                >
                                    <OverlayLabel color="primary.light">
                                        {coord}:
                                    </OverlayLabel>
                                    <OverlayLabel>
                                        {coord === axis
                                            ? primaryPosition
                                            : patientSpaceCoords?.coords?.[index] !== undefined
                                                ? (patientSpaceCoords?.coords?.[index] / 10).toFixed(2)
                                                : NaN}{' '}
                                    </OverlayLabel>
                                    <OverlayLabel> cm</OverlayLabel>
                                </Box>
                            ))}
                        </React.Fragment>
                    )
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns={'auto 1fr auto'}
                        gap={0.5}
                    >
                        <OverlayLabel color="primary.light">
                            {axis} :
                        </OverlayLabel>
                        <OverlayLabel sx={{ justifyContent: 'flex-end' }}>
                            {primaryPosition}{' '}
                        </OverlayLabel>
                        <OverlayLabel> cm</OverlayLabel>
                    </Box>
                )}
            </OverlayRow>
        </Box>
    );
};

export default CoordinateDisplay;
